import { Button } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { useState, useRef } from 'react'
import {
    Control,
    Controller,
    FieldValues,
    Path,
    RegisterOptions
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Icons } from '@/components/icons'

interface FileUploadControlProps<T extends FieldValues> {
    /**
     * Maximum file size in MB
     */
    maxSize?: number
    allowedTypes?: string[]
    onFileSelect?: (file: File | null) => void
    className?: string
    disabled?: boolean

    name: Path<T>
    control: Control<T>
    rules?: RegisterOptions

    multiple?: boolean
    maxFiles?: number

    existingFile?: string | File
    displayName?: string
    onDeleteExisting?: () => void
    onDownload?: () => void
    onUpload?: () => void
    onChange?: (file: File | null) => void
    onValidationError?: (message: string) => void
    clearAfterSelect?: boolean
}

// eslint-disable-next-line max-lines-per-function
export function FileUploadControl<T extends FieldValues>({
    maxSize = 5,
    allowedTypes = ['.pdf'],
    onFileSelect,
    className = '',
    disabled = false,
    name,
    control,
    rules,
    multiple = false,
    maxFiles = 1,
    existingFile,
    displayName,
    onDeleteExisting,
    onDownload,
    onUpload,
    onChange,
    onValidationError,
    clearAfterSelect = false
}: FileUploadControlProps<T>): React.ReactElement {
    const { t } = useTranslation()
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const validateFiles = (files: File[]): boolean | string => {
        if (files.length > maxFiles) {
            const errorMsg = t('uploadFiles.maximumFiles', { max: maxFiles })

            onValidationError?.(errorMsg)

            return errorMsg
        }

        for (const file of files) {
            if (file.size > maxSize * 1024 * 1024) {
                const errorMsg = t('uploadFiles.fileTooLarge', {
                    fileName: file.name,
                    size: maxSize
                })

                onValidationError?.(errorMsg)

                return errorMsg
            }
            if (
                !allowedTypes.some((type) =>
                    file.name.toLowerCase().endsWith(type)
                )
            ) {
                const errorMsg = `El archivo ${file.name} debe ser de tipo: ${allowedTypes.join(', ')}`

                onValidationError?.(errorMsg)

                return errorMsg
            }
        }

        return true
    }

    const handleFiles = (fileList: FileList | null): null | File | File[] => {
        if (!fileList?.length) {
            return null
        }

        const filesArray = Array.from(fileList)
        const validationResult = validateFiles(filesArray)

        if (typeof validationResult === 'string') {
            // El toast ya se mostró en validateFiles, solo retornar null
            return null
        }

        if (multiple) {
            return filesArray.slice(0, maxFiles)
        }

        return filesArray[0]
    }

    const renderExistingFile = () => {
        if (!existingFile) {
            return null
        }

        return (
            <div className="flex items-center justify-between p-2 border rounded mb-2">
                <div className="flex items-center gap-2">
                    <Icons.File className="w-8 h-8 text-primary" />
                    <p className="text-sm font-medium">
                        {displayName
                            ? displayName
                            : typeof existingFile === 'string'
                              ? existingFile
                              : existingFile.name}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        color="primary"
                        isIconOnly
                        onPress={() => {
                            if (typeof existingFile === 'string') {
                                if (onDownload) {
                                    onDownload()
                                } else {
                                    window.open(existingFile, '_blank')
                                }
                            }
                        }}
                    >
                        <Icons.Download />
                    </Button>
                    <Button
                        size="sm"
                        color="danger"
                        isIconOnly
                        onPress={onDeleteExisting}
                        isDisabled={disabled}
                    >
                        <Icons.Times />
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                validate: {
                    files: (value) => {
                        if (!value) {
                            return true
                        }
                        const files = Array.isArray(value) ? value : [value]

                        return validateFiles(files)
                    }
                },
                ...rules
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <motion.div className={`w-full ${className}`}>
                    {renderExistingFile()}
                    <div
                        className={`relative p-6 border-2 border-dashed rounded-lg
                                ${isDragging ? 'border-primary bg-primary/10' : 'border-gray-300'}
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                ${error ? 'border-danger' : ''}`}
                        onDrop={(e) => {
                            e.preventDefault()
                            setIsDragging(false)
                            const result = handleFiles(e.dataTransfer.files)

                            onChange(result)
                            if (!multiple) {
                                onFileSelect?.(result)
                                if (clearAfterSelect) {
                                    onChange(null)
                                }
                            }
                        }}
                        onDragOver={(e) => {
                            e.preventDefault()
                            setIsDragging(true)
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onClick={() =>
                            !disabled && fileInputRef.current?.click()
                        }
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept={allowedTypes.join(',')}
                            multiple={multiple}
                            onChange={(e) => {
                                const result = handleFiles(e.target.files)

                                onChange(result)
                                if (!multiple) {
                                    onFileSelect?.(result)
                                    if (clearAfterSelect) {
                                        onChange(null)
                                    }
                                }
                            }}
                            disabled={disabled}
                        />
                        <div className="flex flex-col items-center gap-2">
                            {value ? (
                                <div className="w-full space-y-2">
                                    {(multiple
                                        ? (value as File[])
                                        : [value as File]
                                    ).map((file, index) => (
                                        <div
                                            key={file.name + index}
                                            className="flex items-center justify-between p-2 border rounded"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icons.File className="w-8 h-8 text-primary" />
                                                <p className="text-sm font-medium">
                                                    {file.name}
                                                </p>
                                            </div>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                isIconOnly
                                                onPress={() => {
                                                    if (multiple) {
                                                        const files = (
                                                            value as File[]
                                                        ).filter(
                                                            (_, i) =>
                                                                i !== index
                                                        )

                                                        onChange(
                                                            files.length
                                                                ? files
                                                                : null
                                                        )
                                                        onFileSelect?.(
                                                            files.length
                                                                ? files[0]
                                                                : null
                                                        )
                                                    } else {
                                                        onChange(null)
                                                        onFileSelect?.(null)
                                                    }
                                                }}
                                            >
                                                <Icons.Times />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center">
                                    <Icons.Upload className="w-8 h-8 text-gray-400 mx-auto" />
                                    <p className="mt-2 text-sm text-gray-500">
                                        {t('uploadFiles.dragAndDropOrClick')}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {allowedTypes.join(', ')}{' '}
                                        {t('uploadFiles.maxFileSize', {
                                            size: maxSize
                                        })}
                                    </p>
                                </div>
                            )}
                        </div>
                        {error && (
                            <p className="mt-2 text-sm text-danger">
                                {error.message}*
                            </p>
                        )}
                    </div>
                </motion.div>
            )}
        />
    )
}
